import { QueryExecutionContext } from './analytics';
/**
 * User-facing grouping of `QueryExecutionContext` values by what triggered the
 * run: a person acting directly, an app surface loading many queries at once
 * (dashboards, embeds, data apps), or a schedule running in the background.
 */
export var QueryTrigger;
(function (QueryTrigger) {
    QueryTrigger["INTERACTIVE"] = "interactive";
    QueryTrigger["APPS"] = "apps";
    QueryTrigger["SCHEDULED"] = "scheduled";
})(QueryTrigger || (QueryTrigger = {}));
/**
 * Every context maps to exactly one trigger — no fallback bucket, so adding a
 * new `QueryExecutionContext` value fails compilation until it is classified.
 */
export const QUERY_TRIGGER_BY_CONTEXT = {
    [QueryExecutionContext.EXPLORE]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.SQL_RUNNER]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.COMPOSE_SQL_RUNNER]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.PYTHON_RUNNER]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.MULTI_SOURCE_QUERY]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.CHART]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.CHART_HISTORY]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.SQL_CHART]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.VIEW_UNDERLYING_DATA]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.CALCULATE_TOTAL]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.CALCULATE_SUBTOTAL]: QueryTrigger.INTERACTIVE,
    // api/cli/ai/mcp are executed by the user, just through another door.
    [QueryExecutionContext.API]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.CLI]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.GSHEETS_ADDON]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.DESKTOP]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.AI]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.MCP_RUN_METRIC_QUERY]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.MCP_RUN_SQL]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.MCP_SEARCH_FIELD_VALUES]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.MCP_QUERY]: QueryTrigger.INTERACTIVE,
    [QueryExecutionContext.DASHBOARD]: QueryTrigger.APPS,
    // Metric cards render themselves on load — homepage KPI blocks and the
    // metrics catalog, not a person hitting run.
    [QueryExecutionContext.METRICS_EXPLORER]: QueryTrigger.APPS,
    [QueryExecutionContext.AUTOREFRESHED_DASHBOARD]: QueryTrigger.APPS,
    [QueryExecutionContext.FILTER_AUTOCOMPLETE]: QueryTrigger.APPS,
    [QueryExecutionContext.DATA_APP_SAMPLE]: QueryTrigger.APPS,
    [QueryExecutionContext.EMBED]: QueryTrigger.APPS,
    [QueryExecutionContext.ALERT]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_DELIVERY]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.CSV]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.GSHEETS]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_GSHEETS_CHART]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_GSHEETS_DASHBOARD]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_GSHEETS_SQL_CHART]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_CHART]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.SCHEDULED_DASHBOARD]: QueryTrigger.SCHEDULED,
    [QueryExecutionContext.PRE_AGGREGATE_MATERIALIZATION]: QueryTrigger.SCHEDULED,
};
export const getQueryTrigger = (context) => QUERY_TRIGGER_BY_CONTEXT[context];
export const getContextsForTrigger = (trigger) => Object.values(QueryExecutionContext).filter((context) => QUERY_TRIGGER_BY_CONTEXT[context] === trigger);
export var QueryLanguage;
(function (QueryLanguage) {
    QueryLanguage["SEMANTIC"] = "semantic";
    QueryLanguage["SQL"] = "sql";
})(QueryLanguage || (QueryLanguage = {}));
/**
 * Request-parameter keys that identify a raw-SQL run. SQL runner queries store
 * `sql`, saved SQL charts store `savedSqlUuid` or `slug` — everything else is
 * a semantic (metric query) run. Shared with the backend so the SQL `WHERE`
 * clause and the TS derivation cannot drift apart.
 */
export const SQL_LANGUAGE_REQUEST_PARAMETER_KEYS = [
    'sql',
    'savedSqlUuid',
    'slug',
];
/**
 * Time windows for the query history list, newest first. Windows are disjoint:
 * "last hour" excludes the last few minutes, and so on. Anything older than 30
 * days is out of scope for the page.
 */
export var QueryHistoryWindow;
(function (QueryHistoryWindow) {
    QueryHistoryWindow["LAST_FEW_MINUTES"] = "lastFewMinutes";
    QueryHistoryWindow["LAST_HOUR"] = "lastHour";
    QueryHistoryWindow["LAST_24_HOURS"] = "last24Hours";
    QueryHistoryWindow["LAST_7_DAYS"] = "last7Days";
    QueryHistoryWindow["LAST_30_DAYS"] = "last30Days";
})(QueryHistoryWindow || (QueryHistoryWindow = {}));
/** Window upper bound in minutes back from now; lower bound is the previous window. */
export const QUERY_HISTORY_WINDOW_MINUTES = {
    [QueryHistoryWindow.LAST_FEW_MINUTES]: 5,
    [QueryHistoryWindow.LAST_HOUR]: 60,
    [QueryHistoryWindow.LAST_24_HOURS]: 60 * 24,
    [QueryHistoryWindow.LAST_7_DAYS]: 60 * 24 * 7,
    [QueryHistoryWindow.LAST_30_DAYS]: 60 * 24 * 30,
};
export const QUERY_HISTORY_WINDOWS_ORDERED = [
    QueryHistoryWindow.LAST_FEW_MINUTES,
    QueryHistoryWindow.LAST_HOUR,
    QueryHistoryWindow.LAST_24_HOURS,
    QueryHistoryWindow.LAST_7_DAYS,
    QueryHistoryWindow.LAST_30_DAYS,
];
export var QueryHistorySortBy;
(function (QueryHistorySortBy) {
    QueryHistorySortBy["CREATED_AT"] = "createdAt";
    QueryHistorySortBy["RUNTIME"] = "runtime";
})(QueryHistorySortBy || (QueryHistorySortBy = {}));
//# sourceMappingURL=queryHistoryList.js.map